/**
 * DeleteTagDialog Component
 * 
 * Диалог подтверждения удаления тега с информацией о количестве записей,
 * в которых используется этот тег
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { TagChip } from './TagChip';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import type { Tag } from '@/integrations/supabase/types';
import { useTranslation } from "react-i18next";

interface DeleteTagDialogProps {
  tag: Tag | null;
  open: boolean;
  onClose: () => void;
  onConfirm: (tagId: string) => void;
  isDeleting?: boolean;
}

export const DeleteTagDialog: React.FC<DeleteTagDialogProps> = ({
  tag,
  open,
  onClose,
  onConfirm,
  isDeleting = false
}) => {
  const { t } = useTranslation();
  const [recordCount, setRecordCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Загружаем количество записей с этим тегом
  useEffect(() => {
    const fetchRecordCount = async () => {
      if (!tag || !open) {
        setRecordCount(null);
        setError(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const { count, error } = await supabase
          .from('record_tags')
          .select('*', { count: 'exact', head: true })
          .eq('tag_id', tag.id);

        if (error) throw error;

        setRecordCount(count || 0);
      } catch (err) {
        console.error('Error fetching record count:', err);
        setError(t('records.dialogs.delete_tag.fetch_error'));
        setRecordCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchRecordCount();
  }, [tag, open, t]);

  const handleConfirm = () => {
    if (!tag) return;
    onConfirm(tag.id);
  };

  const handleClose = () => {
    if (isDeleting) return;

    setRecordCount(null);
    setError(null);
    onClose();
  };

  if (!tag) return null;

  const getRecordCountText = (count: number) => {
    if (count === 0) return t('records.dialogs.delete_tag.unused');
    if (count === 1) return t('records.dialogs.delete_tag.used_single', { count });
    if (count >= 2 && count <= 4) return t('records.dialogs.delete_tag.used_few', { count });
    return t('records.dialogs.delete_tag.used_many', { count });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader className="space-y-3">
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />
            {t('records.dialogs.delete_tag.title')}
          </DialogTitle>
          <DialogDescription>
            {t('records.dialogs.delete_tag.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Отображение тега */}
          <div className="p-3 bg-muted/30 rounded-lg border">
            <div className="flex items-center justify-center">
              <TagChip tag={tag} size="md" />
            </div>
          </div>

          {/* Информация об использовании */}
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <LoadingSpinner message={t('records.dialogs.delete_tag.checking')} />
            </div>
          ) : error ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : recordCount !== null ? (
            <Alert variant={recordCount > 0 ? "destructive" : "default"}>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {getRecordCountText(recordCount)}
                {recordCount > 0 && (
                  <>
                    <br />
                    <strong>{t('records.dialogs.delete_tag.warning')}</strong>
                  </>
                )}
              </AlertDescription>
            </Alert>
          ) : null}
        </div>

        {/* Кнопки действий */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            {recordCount !== null && recordCount > 0 && (
              <span className="text-destructive">
                {t('records.dialogs.delete_tag.impact_warning', { count: recordCount })}
              </span>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isDeleting}
              size="sm"
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={isDeleting || loading}
              className="min-w-[100px]"
              size="sm"
            >
              {isDeleting ? (
                <>
                  <LoadingSpinner className="h-3 w-3 mr-2" />
                  {t('records.dialogs.delete_tag.deleting')}
                </>
              ) : (
                <>
                  <Trash2 className="h-3 w-3 mr-2" />
                  {t('records.dialogs.delete_tag.delete')}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};